import React, { createContext, useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL, getCurrentBackendUrl, getBackendUrl } from '../config';
import { getStoredRole } from '../authority'; // Update to only import exported functions

// Create the authentication context
export const AuthContext = createContext();

// The API returns the token already prefixed with "Bearer ". Storing it that way
// made the ~24 call sites that send `Bearer ${jstoken}` produce
// "Bearer Bearer <jwt>": the API read "Bearer" as the token, failed to decode it
// and fell back to an anonymous session, so saved quiz results, mistakes and
// vocabulary were never attached to the signed-in user. Store the bare token;
// the API accepts a bare token as well.
const stripBearer = (token) =>
  typeof token === "string" ? token.replace(/^Bearer\s+/i, "") : token;

// Helper function to extract role from JWT token (copied from authority.js since it's not exported)
const extractRoleFromToken = (token) => {
  try {
    if (!token) return null;

    // Remove Bearer prefix if present
    const actualToken = token.startsWith("Bearer ") ? token.slice(7) : token;
    const tokenParts = actualToken.split('.');

    if (tokenParts.length !== 3) return null;

    // Base64 decode and parse the payload
    const payload = JSON.parse(atob(tokenParts[1]));

    // Extract the role using all possible field names
    const role = payload.user_role || payload.role || payload.userRole ||
      payload.authorization ||
      (payload.sub && payload.sub.user_role);

    // Normalize role - if it's a string with only digits, convert to number
    if (typeof role === 'string' && /^\d+$/.test(role)) {
      return parseInt(role, 10);
    }
    return role;
  } catch (error) {
    console.error("Error extracting role from token:", error);
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [backendUrl, setBackendUrl] = useState(getCurrentBackendUrl());
  const [authChecked, setAuthChecked] = useState(false); // Flag to track if auth check has completed

  // Initialize the backend URL
  useEffect(() => {
    const initBackend = async () => {
      const url = await getBackendUrl();
      setBackendUrl(url);
    };
    initBackend();
  }, []);

  // Listen for storage changes (for multi-tab support)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'jstoken') {
        if (!e.newValue) {
          // Token was removed in another tab
          setCurrentUser(null);
        } else if (!localStorage.getItem('jstoken')) {
          // Token was added in another tab
          checkUserLoggedIn();
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Force refresh user data from server
  const refreshUserData = async () => {
    try {
      const token = localStorage.getItem('jstoken');
      if (!token) return false;

      const url = await getBackendUrl();
      const response = await axios.get(`${url}/user/me`, {
        headers: {
          Authorization: token.startsWith('Bearer ') ? token : `Bearer ${token}`
        }
      });

      if (response.data && response.data.user) {
        setCurrentUser({
          ...response.data.user,
          token: token
        });
        return true;
      }
    } catch (error) {
      console.error("Failed to refresh user data:", error);
    }
    return false;
  };

  // Check if the user is logged in
  const checkUserLoggedIn = async (skipApiCall = false) => {
    try {
      const token = localStorage.getItem('jstoken');
      const userRole = localStorage.getItem('user_role');

      // If no token exists, user is not logged in
      if (!token) {
        setCurrentUser(null);
        setLoading(false);
        setAuthChecked(true);
        return;
      }

      // First try to create a user object from local storage
      // This ensures we have something even if network calls fail
      const localUserData = {
        role: userRole ? parseInt(userRole, 10) : null,
        user_role: userRole ? parseInt(userRole, 10) : null,
        token: token,
        lastSession: localStorage.getItem('session'),
        // Try to get cached user data from localStorage
        user_name: localStorage.getItem('user_name'),
        user_email_address: localStorage.getItem('user_email_address'),
        user_id: localStorage.getItem('user_id')
      };

      // Set user from local storage immediately to prevent flickering
      setCurrentUser(localUserData);

      // Skip API validation if requested (during development or for performance)
      if (skipApiCall) {
        setLoading(false);
        setAuthChecked(true);
        return;
      }

      // Validate token with server (with retry and timeout)
      try {
        const url = await getBackendUrl();
        setBackendUrl(url);

        // Make request with timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000); // Increased timeout

        const response = await axios.get(`${url}/user/me`, {
          headers: {
            Authorization: token.startsWith('Bearer ') ? token : `Bearer ${token}`
          },
          signal: controller.signal
        });

        clearTimeout(timeoutId);

        // Handle successful response
        if (response.data) {
          let apiUserData = null;
          
          // Extract user data from different response formats
          if (response.data.success && response.data.user) {
            // Format: { success: true, user: {...} }
            apiUserData = response.data.user;
          } else if (response.data.user) {
            // Format: { user: {...} } - This is the actual backend format
            apiUserData = response.data.user;
          } else if (response.data.user_id) {
            // Format: { user_id: "...", user_name: "...", etc }
            apiUserData = response.data;
          }

          if (apiUserData) {
            // Prioritize localStorage data over API data to prevent overwriting fresh login data
            // Only update localStorage if API data is different and seems more recent
            const localUserName = localStorage.getItem('user_name');
            const localUserEmail = localStorage.getItem('user_email_address');
            const localUserId = localStorage.getItem('user_id');
            
            // Create merged user data, prioritizing localStorage for core user info
            const localUserRole = localStorage.getItem('user_role');
            const userData = {
              ...apiUserData,
              token: token,
              // Prioritize localStorage data for user identity fields
              user_name: localUserName || apiUserData.user_name,
              user_email_address: localUserEmail || apiUserData.user_email_address,
              user_id: localUserId || apiUserData.user_id,
              // Prioritize localStorage role over API data to prevent overwriting fresh login data
              user_role: localUserRole ? parseInt(localUserRole) : apiUserData.user_role,
              role: localUserRole ? parseInt(localUserRole) : apiUserData.user_role
            };
            
            setCurrentUser(userData);

            // Only update localStorage if we don't have the data or API data seems newer
            if (apiUserData.user_role && !localStorage.getItem('user_role')) {
              localStorage.setItem('user_role', apiUserData.user_role.toString());
            }
            if (apiUserData.user_name && !localUserName) {
              localStorage.setItem('user_name', apiUserData.user_name);
            }
            if (apiUserData.user_email_address && !localUserEmail) {
              localStorage.setItem('user_email_address', apiUserData.user_email_address);
            }
            if (apiUserData.user_id && !localUserId) {
              localStorage.setItem('user_id', apiUserData.user_id.toString());
            }
          } else {
            // If we can't parse the API response, stick with localStorage data
            setCurrentUser(localUserData);
          }
        }
      } catch (reqError) {
        console.warn("API validation failed, using local token data:", reqError);

        // Don't log out the user on network errors - the token might still be valid
        // Only clear authentication if the server explicitly rejects the token
        if (reqError.response && reqError.response.status === 401) {
          localStorage.removeItem('jstoken');
          localStorage.removeItem('user_role');
          localStorage.removeItem('user_name');
          localStorage.removeItem('user_email_address');
          localStorage.removeItem('user_id');
          localStorage.removeItem('session');
          setCurrentUser(null);
        } else {
          // For network errors or timeouts, keep the user logged in with local data
          // We already set currentUser from localStorage above
        }
      }
    } catch (err) {
      console.error("Auth verification error:", err);

      // Try to extract role from token as a last resort
      const token = localStorage.getItem('jstoken');
      if (token) {
        try {
          const role = extractRoleFromToken(token);
          if (role) {
            setCurrentUser({
              role: role,
              token: token
            });
          }
        } catch (tokenErr) {
          console.error("Failed to parse token:", tokenErr);
        }
      }
    } finally {
      setLoading(false);
      setAuthChecked(true);
    }
  };

  // Run auth check when component mounts
  useEffect(() => {
    checkUserLoggedIn();
  }, []);

  // Login function with retry logic
  const login = async (user_email_address, user_password) => {
    try {
      setLoading(true);

      // Get the latest backend URL
      const url = await getBackendUrl();
      setBackendUrl(url);

      const response = await axios.post(`${url}/user/login`, {
        user_email_address,
        user_password
      });

      const data = response.data;

      if (data.jwt) {
        const token = typeof data.jwt === 'object' ? data.jwt.token : data.jwt;
      localStorage.setItem('jstoken', stripBearer(token));

        if (data.user_role) {
          localStorage.setItem('user_role', data.user_role.toString());
        }

        if (data.last_session) {
          localStorage.setItem('session', data.last_session);
        }

        // Store user data in localStorage for persistence
        if (data.user_name) {
          localStorage.setItem('user_name', data.user_name);
        }
        if (data.user_email_address) {
          localStorage.setItem('user_email_address', data.user_email_address);
        }
        if (data.user_id) {
          localStorage.setItem('user_id', data.user_id.toString());
        }

        setCurrentUser({
          role: data.user_role,
          user_role: data.user_role,
          token: token,
          user_name: data.user_name,
          user_id: data.user_id,
          user_email_address: data.user_email_address
        });
        setError(null);

        return {
          success: true,
          user_role: data.user_role
        };
      } else if (data.needsVerification) {
        return {
          success: false,
          needsVerification: true,
          email: data.email
        };
      } else {
        throw new Error("Invalid response from server");
      }
    } catch (err) {
      console.error("Login error:", err);

      // Special handling for network errors
      if (err.code === 'ERR_NETWORK') {
        setError("Cannot connect to server. Please check your internet connection or try again later.");
        return {
          success: false,
          error: "Cannot connect to server. Please check your internet connection or try again later."
        };
      }

      // setError is handled by the component calling login

      return {
        success: false,
        error: err.response?.data?.msg || err.message || "Login failed"
      };
    } finally {
      setLoading(false);
    }
  };

  // Register function with improved error handling
  const register = async (userData) => {
    try {
      setLoading(true);

      // Get the latest backend URL
      const url = await getBackendUrl();
      setBackendUrl(url);

      const response = await axios.post(`${url}/user/register`, userData);
      console.log('Registration response:', response.data);
      return { success: true, message: response.data.msg };
    } catch (err) {
      if (err.response && err.response.status === 409) {
        try {
          await resendOtp(userData.user_email_address);
          return { success: true, message: "User already exists. OTP has been sent to your email." };
        }
        catch (otpErr) {
          return { success: false, message: otpErr.response?.data?.message || "Failed to send OTP" };
        }
      } else if (err.code === 'ERR_NETWORK') {
        setError("Cannot connect to server. Please check your internet connection or try again later.");
        return {
          success: false,
          message: "Cannot connect to server. Please check your internet connection or try again later."
        };
      }
      console.error("Registration error:", err);
      setError(err.response?.data?.message || "Registration failed. Please try again.");
      return { success: false, message: err.response?.data?.message || "Registration failed" };
    } finally {
      setLoading(false);
    }
  };

  async function resendOtp(userEmail) {
    try {
      // Get the latest backend URL
      const url = await getBackendUrl();
      setBackendUrl(url);

      const response = await fetch(`${url}/user/resend-otp/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_email_address: userEmail,
        }),
      });
      const data = await response.json();
      console.log('OTP resend success:', data);
      return data;
    } catch (error) {
      console.error('OTP resend failed:', error.message);
      throw error;  // Re-throw to allow proper error handling in calling function
    }
  }

  async function verifyOtp(userEmail, otpCode) {
    try {
      // Get the latest backend URL
      const url = await getBackendUrl();
      setBackendUrl(url);

      const response = await axios.post(`${url}/user/verify-code`, {
        user_email_address: userEmail,
        user_activation_code: otpCode,
      });

      const data = response.data;
      console.log('OTP verification success:', data);

      // Store JWT token correctly
      localStorage.setItem('jstoken', stripBearer(data.jwt?.token || data.jwt));

      // Store user role if available
      if (data.user_role) {
        localStorage.setItem('user_role', data.user_role.toString());
      }

      // Store session if provided
      if (data.last_session) {
        localStorage.setItem('session', data.last_session);
      }

      // Store user data in localStorage for persistence
      if (data.user_name) {
        localStorage.setItem('user_name', data.user_name);
      }
      if (data.user_email_address) {
        localStorage.setItem('user_email_address', data.user_email_address);
      }
      if (data.user_id) {
        localStorage.setItem('user_id', data.user_id.toString());
      }

      // Update current user state
      setCurrentUser({
        role: data.user_role,
        user_role: data.user_role,
        token: data.jwt?.token || data.jwt,
        user_name: data.user_name,
        user_id: data.user_id,
        user_email_address: data.user_email_address
      });

      setError(null);
      return data;
    } catch (error) {
      console.error('OTP verification failed:', error.message);

      if (error.code === 'ERR_NETWORK') {
        setError("Cannot connect to server. Please check your internet connection or try again later.");
      }

      return null;
    }
  }

  // Logout function
  const logout = () => {
    localStorage.removeItem('jstoken');
    localStorage.removeItem('user_role');
    localStorage.removeItem('user_name');
    localStorage.removeItem('user_email_address');
    localStorage.removeItem('user_id');
    localStorage.removeItem('session');
    setCurrentUser(null);
  };

  // Password recovery function
  const recoverPassword = async (email) => {
    try {
      setLoading(true);

      // Get the latest backend URL
      const url = await getBackendUrl();
      setBackendUrl(url);

      const response = await axios.post(`${url}/user/reset-password`, { user_email_address: email });

      if (response.data && response.data.success) {
        return { success: true, message: response.data.message };
      } else {
        setError(response.data.message || "Password recovery failed");
        return { success: false, message: response.data.message };
      }
    } catch (err) {
      console.error("Password recovery error:", err);

      if (err.code === 'ERR_NETWORK') {
        setError("Cannot connect to server. Please check your internet connection or try again later.");
        return {
          success: false,
          message: "Cannot connect to server. Please check your internet connection or try again later."
        };
      }

      setError(err.response?.data?.message || "Password recovery failed. Please try again.");
      return { success: false, message: err.response?.data?.message || "Password recovery failed" };
    } finally {
      setLoading(false);
    }
  };

  // Reset password function
  const resetPassword = async (token, password) => {
    console.log(token, password);
    try {
      setLoading(true);

      // Get the latest backend URL
      const url = await getBackendUrl();
      setBackendUrl(url);
      const data={
        "token": token,
        "user_password": password
      }
      const response = await axios.post(`${url}/user/resetpassword/`,data );

      if (response.data && response.data.success) {
        return { success: true, message: response.data.message };
      } else {
        setError(response.data.message || "Password reset failed");
        return { success: false, message: response.data.message };
      }
    } catch (err) {
      console.error("Password reset error:", err);

      if (err.code === 'ERR_NETWORK') {
        setError("Cannot connect to server. Please check your internet connection or try again later.");
        return {
          success: false,
          message: "Cannot connect to server. Please check your internet connection or try again later."
        };
      }

      setError(err.response?.data?.message || "Password reset failed. Please try again.");
      return { success: false, message: err.response?.data?.message || "Password reset failed" };
    } finally {
      setLoading(false);
    }
  };

  // Update user profile function
  const updateProfile = async (userData) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('jstoken');

      // Get the latest backend URL
      const url = await getBackendUrl();
      setBackendUrl(url);

      if (!token) {
        throw new Error("Authentication token is missing");
      }

      const response = await axios.put(`${url}/users/profile`, userData, {
        headers: {
          Authorization: token.startsWith('Bearer ') ? token : `Bearer ${token}`
        }
      });

      if (response.data && response.data.success) {
        setCurrentUser({ ...currentUser, ...userData });
        return { success: true, message: response.data.message };
      } else {
        setError(response.data.message || "Profile update failed");
        return { success: false, message: response.data.message };
      }
    } catch (err) {
      console.error("Profile update error:", err);

      if (err.code === 'ERR_NETWORK') {
        setError("Cannot connect to server. Please check your internet connection or try again later.");
        return {
          success: false,
          message: "Cannot connect to server. Please check your internet connection or try again later."
        };
      }

      setError(err.response?.data?.message || "Profile update failed. Please try again.");
      return { success: false, message: err.response?.data?.message || "Profile update failed" };
    } finally {
      setLoading(false);
    }
  };

  // Value to be provided to consumers of this context
  const value = {
    currentUser,
    loading,
    error,
    login,
    resendOtp,
    register,
    logout,
    recoverPassword,
    resetPassword,
    updateProfile,
    verifyOtp,
    backendUrl,
    checkUserLoggedIn,  // Expose this function to allow manual auth checks
    refreshUserData,    // Expose refresh function
    isAuthenticated: !!currentUser, // Convenience boolean
    authChecked        // Flag to indicate auth check has completed
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};